import { FoodsIcon, FoodsIconButton, NumberWithUnit, ScatterPlot } from "@foodpilot/foods";
import { Box, Button, CircularProgress, Stack, Typography, useTheme } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import EmptySetIcon from "src/assets/icons/empty-set.svg?react";
import { useApplicationParameters } from "src/api/client-api/appParams";
import type { OlapScores, OlapScoresWithPrevScores } from "src/api/client-api/olapScores/olapScoresSchema";
import { ScoreSourceChip } from "src/components/Score/ScoreSourceChip";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import type { ScoreWithVariation } from "src/services/ScoreService";
import { formatNumber } from "src/utils/formatting";
import type { ReferenceEntityData } from "./ScoreScatterPlot/ScoreScatterPlot";
import { getScoreScatterAxisDomain, getScoreScatterDataset, type VisualisationChoice } from "./scoreScatterData";

export type ProductScoreOverviewDetailsProps<T extends VisualisationChoice> = {
    scores: OlapScoresWithPrevScores | undefined;
    areScoresRefetching?: boolean;
    scatteredScores: OlapScores | undefined;
    areScatteredScoresRefetching?: boolean;
    scatteredScoresDimension: string;
    getActivityDataValueByCompanyId: (companyId: number, visualisationChoiceKey: T["key"]) => number;
    scoreWithVariation: ScoreWithVariation;
    visualisationChoices: T[];
    dataLabel: string;
    onScoreDistributionClick?: (scoreId: number) => void;
    referenceScores?: Record<number, number>;
    atLeastOnePostLocked?: boolean;
    referenceEntityData?: ReferenceEntityData;
    isFirstScoreSelected?: boolean;
};

export const ProductScoreOverviewDetails = <T extends VisualisationChoice>(
    props: ProductScoreOverviewDetailsProps<T>,
) => {
    const {
        scores,
        areScoresRefetching = false,
        scatteredScores,
        areScatteredScoresRefetching = false,
        scatteredScoresDimension,
        getActivityDataValueByCompanyId,
        scoreWithVariation,
        visualisationChoices,
        dataLabel,
        onScoreDistributionClick,
        atLeastOnePostLocked = false,
        referenceEntityData: { aggregateContributors = [] } = {},
        isFirstScoreSelected = true,
    } = props;
    const theme = useTheme();
    const { t, i18n } = useTranslation();
    const { isFeatureActivated } = useApplicationParameters();
    const visualisationChoice = visualisationChoices[0];
    const score = scores?.olapScores[scoreWithVariation.scoreId];
    const scoreValue = OlapScoresService.normalizeNumericalScore(score?.score.value);
    const specificityScore = OlapScoresService.getSubscoreWithType("specificity-rate", score);
    const specificityValue = OlapScoresService.normalizeNumericalScore(specificityScore?.score.value);
    const language = i18n.resolvedLanguage ?? i18n.language ?? "en";
    const formattedScore =
        scoreValue === null ? null : (
            formatNumber(scoreValue, language, { maximumFractionDigits: scoreWithVariation.precision })
        );
    const dataset = useMemo(
        () =>
            visualisationChoice && !areScatteredScoresRefetching ?
                getScoreScatterDataset(
                    scatteredScores?.olapScores[scoreWithVariation.scoreId],
                    scatteredScoresDimension,
                    visualisationChoice,
                    getActivityDataValueByCompanyId,
                )
            :   [],
        [
            areScatteredScoresRefetching,
            getActivityDataValueByCompanyId,
            scatteredScores,
            scatteredScoresDimension,
            scoreWithVariation.scoreId,
            visualisationChoice,
        ],
    );
    const scatterAxisDomain = useMemo(() => getScoreScatterAxisDomain(dataset), [dataset]);
    const shouldRenderSourceChip = aggregateContributors.some(
        ({ source }) => source === "supplier_reference" || source === "ingredient_reference" || source === "sure",
    );
    const canDrilldown =
        isFeatureActivated("FS-product-score-details-distribution") &&
        scoreValue !== null &&
        onScoreDistributionClick !== undefined;

    return (
        <Stack
            data-testid="product-score-overview-details"
            sx={{
                backgroundColor: theme.palette.primary[800],
                borderBottomLeftRadius: theme.spacing(1.5),
                borderBottomRightRadius: theme.spacing(1.5),
                borderTopLeftRadius: isFirstScoreSelected ? theme.spacing(0) : theme.spacing(1.5),
                borderTopRightRadius: theme.spacing(1.5),
                padding: theme.spacing(2),
                width: "100%",
            }}
        >
            <Stack
                data-testid="product-score-overview-details-surface"
                direction={{ xs: "column", md: "row" }}
                sx={{
                    backgroundColor: theme.custom.grey[100],
                    borderRadius: theme.spacing(2),
                    boxShadow: theme.custom.shadows.grey,
                    gap: theme.spacing(3),
                    padding: theme.spacing(3),
                    position: "relative",
                    width: "100%",
                }}
            >
                <Stack
                    data-testid="product-score-overview-summary"
                    sx={{
                        flexShrink: 0,
                        gap: theme.spacing(3),
                        maxWidth: "100%",
                        width: theme.custom.scoreOverviewCard.width,
                    }}
                >
                    <Stack data-testid="product-score-overview-title-score" gap={theme.spacing(2)}>
                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                            <Typography variant="body-semibold">
                                {t(
                                    "product.v2.scores.overview.weighted_average",
                                    "Weighted average across {{count}} companies (by production quantities):",
                                    { count: dataset.length },
                                )}
                            </Typography>
                            {shouldRenderSourceChip && <ScoreSourceChip contributors={aggregateContributors} />}
                        </Stack>

                        {areScoresRefetching ?
                            <CircularProgress />
                        :   <NumberWithUnit
                                value={formattedScore}
                                unit={scoreWithVariation.unit}
                                size="big"
                                emptyLabel={t("product.score_details.score_unavailable", "Score unavailable")}
                            />
                        }
                    </Stack>

                    {atLeastOnePostLocked && specificityValue !== null && (
                        <Stack direction="row" alignItems="center" gap={theme.spacing(0.25)}>
                            <Typography variant="small-semibold">
                                {t("product.v2.scores.overview.specificity_label", "Data specificity rate:")}
                            </Typography>
                            <Typography variant="small-regular">
                                {formatNumber(specificityValue, language, { maximumFractionDigits: 2 })} %
                            </Typography>
                            <FoodsIconButton
                                icon="information"
                                size="xs"
                                tooltip={{
                                    text: t(
                                        "scorecard.specificity_rate.tooltip",
                                        "Shows how much of the available data has been completed. Higher means more accurate results.",
                                    ),
                                }}
                                buttonProps={{
                                    "aria-label": t(
                                        "product.v2.scores.overview.specificity_info",
                                        "About data specificity rate",
                                    ),
                                }}
                            />
                        </Stack>
                    )}

                    {canDrilldown && (
                        <Button
                            fullWidth
                            size="small"
                            variant="secondary"
                            endIcon={<FoodsIcon icon="arrowRightShort" size={2} />}
                            onClick={() => onScoreDistributionClick(scoreWithVariation.scoreId)}
                        >
                            {t("scorecard.see-more-details", "See more details")}
                        </Button>
                    )}
                </Stack>

                <Stack
                    data-testid="product-score-overview-chart"
                    sx={{ backgroundColor: theme.custom.grey[200], flex: 1, gap: theme.spacing(2), minWidth: 0 }}
                >
                    <Stack data-testid="product-score-overview-chart-header">
                        <Typography variant="body-semibold">
                            {t(
                                "product.v2.scores.overview.distribution_title",
                                "Distribution across contributing companies",
                            )}
                        </Typography>
                        <Typography variant="small-regular" color={theme.custom.grey[2500]}>
                            {t(
                                "product.v2.scores.overview.distribution_subtitle",
                                "Click on a company to see its score in detail",
                            )}
                        </Typography>
                    </Stack>

                    <Box sx={{ position: "relative", width: "100%" }}>
                        {visualisationChoice && (
                            <ScatterPlot
                                values={[{ name: dataLabel, data: dataset }]}
                                xMin={scatterAxisDomain?.xMin}
                                xMax={scatterAxisDomain?.xMax}
                                yMin={scatterAxisDomain?.yMin}
                                yMax={scatterAxisDomain?.yMax}
                                height={theme.custom.scoreOverviewCard.details.chartHeight}
                                showLegend={false}
                                surface="embedded"
                                xLabel={visualisationChoice.label}
                                xUnit={visualisationChoice.unit}
                                yLabel={scoreWithVariation.group}
                                yUnit={scoreWithVariation.unit}
                            />
                        )}

                        {(areScatteredScoresRefetching || dataset.length === 0) && (
                            <Stack alignItems="center" justifyContent="center" sx={{ inset: 0, position: "absolute" }}>
                                {areScatteredScoresRefetching ?
                                    <CircularProgress />
                                :   <EmptySetIcon />}
                            </Stack>
                        )}
                    </Box>
                </Stack>

                <Box
                    aria-hidden
                    data-testid="product-score-overview-details-stroke"
                    style={{
                        borderColor: theme.custom.strokes.containerDivider.color,
                        borderRadius: "inherit",
                        borderStyle: theme.custom.strokes.containerDivider.style,
                        borderWidth: theme.custom.strokes.containerDivider.width,
                        boxSizing: "border-box",
                        inset: 0,
                        pointerEvents: "none",
                        position: "absolute",
                    }}
                />
            </Stack>
        </Stack>
    );
};
